import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { toastApiError } from "../../api/apiError"
import { useReextractDeclaration } from "../../api/declarationsApi"

interface DeclarationReextractDialogProps {
  /** The declaration to re-extract, or null when the dialog is closed. */
  declarationId: string | null
  onClose: () => void
  /** Runs after the re-extraction was accepted, before the dialog closes. */
  onStarted?: () => void
}

/** Confirm-before-re-extract (TUR-1474): the last run's staged statements, claims and
 *  article scope are discarded and the pipeline reads the document again. The backend
 *  refuses once the declaration is approved or a reviewer decision exists; that 409
 *  is surfaced here as a toast. */
export function DeclarationReextractDialog({
  declarationId,
  onClose,
  onStarted,
}: DeclarationReextractDialogProps) {
  const { translate } = useTranslation()
  const reextract = useReextractDeclaration()

  const handleConfirm = () => {
    if (!declarationId) return
    reextract.mutate(declarationId, {
      onSuccess: () => {
        toast.success(translate("MC_DECL_REEXTRACT_TOAST"))
        onStarted?.()
        onClose()
      },
      onError: (error) => {
        toastApiError(error, translate, "MC_API_ERROR_GENERIC")
      },
    })
  }

  return (
    <Dialog
      open={declarationId !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("MC_DECL_REEXTRACT_CONFIRM_TITLE")}
          </DialogTitle>
          <DialogDescription>
            {translate("MC_DECL_REEXTRACT_CONFIRM_DESC")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={reextract.isPending}
          >
            {translate("MC_DECL_BTN_CANCEL")}
          </Button>
          <Button onClick={handleConfirm} disabled={reextract.isPending}>
            {reextract.isPending && (
              <Loader2 size={14} className="animate-spin" />
            )}
            {translate("MC_DECL_REEXTRACT_BTN")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
